import React, {
 useState, useEffect
} from 'react';
import {
 useParams
} from 'react-router-dom';
import {
 Label
} from 'shared/components/ui/label';
import {
 Input
} from 'shared/components/ui/input';
import {
 Button
} from 'shared/components/ui/button';
import {
 Checkbox
} from 'shared/components/ui/checkbox';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from 'shared/components/ui/select';
import {
 Plus, Trash2
} from 'lucide-react';
import {
 schemaService
} from 'shared/api/services/BEXO';
import {
 FetchStepConfig, WorkflowFilter
} from '../../types/workflow.types';

interface FetchStepFormProps {
    config: FetchStepConfig;
    onChange: (config: FetchStepConfig) => void;
}

const filterOperators = ['=', '!=', '>', '<', '>=', '<=', 'LIKE', 'IN'] as const;

const FetchStepForm: React.FC<FetchStepFormProps> = ({
 config, onChange
}) => {
    const {
 appId, oragID
} = useParams<{ appId: string; oragID: string }>();
    const [schemaNames, setSchemaNames] = useState<string[]>([]);
    const [schemaColumns, setSchemaColumns] = useState<string[]>([]);
    const [isLoadingSchemas, setIsLoadingSchemas] = useState(true);

    // Load schema names
    useEffect(() => {
        const loadSchemas = async () => {
            if (!appId || !oragID) return;
            try {
                const res = await schemaService.retrieveSchemas('*', appId, oragID);
                const rawSchemas = res.data?.schemas || res.data || [];
                if (Array.isArray(rawSchemas)) {
                    const names = new Set<string>();
                    rawSchemas.forEach((item: any) => {
                        const name = item.tableName || item.table_name;
                        if (name) names.add(name);
                    });
                    setSchemaNames(Array.from(names));
                }
            } catch (error) {
                console.error('Error loading schemas:', error);
            } finally {
                setIsLoadingSchemas(false);
            }
        };
        loadSchemas();
    }, [appId, oragID]);

    // Load columns when schema changes
    useEffect(() => {
        const loadColumns = async () => {
            if (!config.schemaName || !appId || !oragID) {
                setSchemaColumns([]);
                return;
            }
            try {
                const res = await schemaService.retrieveSchemas(config.schemaName, appId, oragID);
                const rawSchemas = res.data?.schemas || res.data || [];
                const columns: string[] = [];
                if (Array.isArray(rawSchemas)) {
                    rawSchemas.forEach((item: any) => {
                        if (Array.isArray(item.columns)) {
                            item.columns.forEach((col: any) => {
                                if (col.columnName) columns.push(col.columnName);
                            });
                        } else if (item.columnName) {
                            columns.push(item.columnName);
                        }
                    });
                }
                setSchemaColumns(columns);
            } catch (error) {
                console.error('Error loading columns:', error);
            }
        };
        loadColumns();
    }, [config.schemaName, appId, oragID]);

    const toggleField = (fieldName: string) => {
        const newFields = config.fields.includes(fieldName)
            ? config.fields.filter((f) => f !== fieldName)
            : [...config.fields, fieldName];
        onChange({
 ...config,
fields: newFields
});
    };

    const addFilter = () => {
        const newFilter: WorkflowFilter = {
 field: '',
operator: '=',
value: ''
};
        onChange({
 ...config,
filters: [...config.filters, newFilter]
});
    };

    const updateFilter = (index: number, update: Partial<WorkflowFilter>) => {
        const newFilters = config.filters.map((f, i) => (i === index ? {
 ...f,
...update
} : f));
        onChange({
 ...config,
filters: newFilters
});
    };

    const removeFilter = (index: number) => {
        onChange({
 ...config,
filters: config.filters.filter((_, i) => i !== index)
});
    };

    return (
        <div className="space-y-4">
            {/* Schema Selector */}
            <div className="space-y-1.5">
                <Label className="text-sm">Schema</Label>
                <Select
                    value={config.schemaName}
                    onValueChange={(v) => onChange({
 ...config,
schemaName: v,
fields: []
})}
                    disabled={isLoadingSchemas}
                >
                    <SelectTrigger>
                        <SelectValue placeholder={isLoadingSchemas ? 'Loading...' : 'Select a schema'} />
                    </SelectTrigger>
                    <SelectContent>
                        {schemaNames.map((name) => (
                            <SelectItem key={name} value={name}>{name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Field Checkboxes */}
            {schemaColumns.length > 0 && (
                <div className="space-y-1.5">
                    <Label className="text-sm">Fields to Fetch</Label>
                    <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border rounded-md bg-muted/20">
                        {schemaColumns.map((col) => (
                            <label key={col} className="flex items-center gap-2 text-sm cursor-pointer">
                                <Checkbox
                                    checked={config.fields.includes(col)}
                                    onCheckedChange={() => toggleField(col)}
                                />
                                <span className="truncate">{col}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                    <Label className="text-sm">Filters</Label>
                    <Button variant="ghost" size="sm" onClick={addFilter} className="h-7 text-xs">
                        <Plus className="mr-1 h-3 w-3" /> Add Filter
                    </Button>
                </div>
                {config.filters.length > 0 && (
                    <div className="space-y-2">
                        {config.filters.map((filter, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                                <Select
                                    value={filter.field}
                                    onValueChange={(v) => updateFilter(idx, {
 field: v
})}
                                >
                                    <SelectTrigger className="flex-1">
                                        <SelectValue placeholder="Field" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {schemaColumns.map((col) => (
                                            <SelectItem key={col} value={col}>{col}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Select
                                    value={filter.operator}
                                    onValueChange={(v) => updateFilter(idx, {
 operator: v as WorkflowFilter['operator']
})}
                                >
                                    <SelectTrigger className="w-20">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {filterOperators.map((op) => (
                                            <SelectItem key={op} value={op}>{op}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Input
                                    value={filter.value}
                                    onChange={(e) => updateFilter(idx, {
 value: e.target.value
})}
                                    placeholder="Value"
                                    className="flex-1"
                                />
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-red-500 shrink-0"
                                    onClick={() => removeFilter(idx)}
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Limit & Order */}
            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                    <Label className="text-sm">Limit</Label>
                    <Input
                        type="number"
                        value={config.limit}
                        onChange={(e) => onChange({
 ...config,
limit: parseInt(e.target.value) || 100
})}
                        min={1}
                    />
                </div>
                <div className="space-y-1.5">
                    <Label className="text-sm">Order By</Label>
                    <div className="flex gap-2">
                        <Select
                            value={config.orderBy}
                            onValueChange={(v) => onChange({
 ...config,
orderBy: v
})}
                        >
                            <SelectTrigger className="flex-1">
                                <SelectValue placeholder="Select field" />
                            </SelectTrigger>
                            <SelectContent>
                                {schemaColumns.map((col) => (
                                    <SelectItem key={col} value={col}>{col}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button
                            variant="outline"
                            size="sm"
                            className="shrink-0 text-xs"
                            onClick={() => onChange({
                                ...config,
                                orderDirection: config.orderDirection === 'asc' ? 'desc' : 'asc',
                            })}
                        >
                            {config.orderDirection === 'asc' ? 'ASC' : 'DESC'}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FetchStepForm;
